import type {
  AttachLegalObligationEvidenceBody,
  AttachLegalObligationEvidenceResponse,
  LegalObligationEvidenceCandidatesResponse,
  LegalObligationEvidencesResponse,
  MaterializeLegalObligationInstanceBody,
  MaterializeLegalObligationInstanceResponse,
  RegisterLegalObligationEventBody,
  RegisterLegalObligationEventResponse,
  SetLegalObligationLastPerformedBody,
  SetLegalObligationLastPerformedResponse,
  PlanLegalObligationInstanceBody,
  PlanLegalObligationInstanceResponse,
  RecordLegalObligationResultBody,
  RecordLegalObligationResultResponse,
  RelinkLegalObligationEvidenceResultBody,
  RelinkLegalObligationEvidenceResultResponse,
  AttachLegalObligationResultEvidenceBody,
  LegalObligationResultEvidencesResponse,
} from '@safetyhub/shared';
import { api, type ApiResponse } from './api';

/**
 * 법정 서류 관리 — **증빙 연결·업로드·소급 기록**의 API 클라이언트(줄기 항목 9-d · L3·L5·L7·L13).
 *
 * 조회 클라이언트 `legalDocuments.ts`와 파일을 가른 이유는 그쪽 docblock과 같다 — 화면 spec이 모듈을
 * `vi.mock`하므로 **순수 술어·라벨을 여기 두지 않는다**(라벨은 `legalDocumentLabels.ts`). 계약의 정본은
 * shared `features/legal-obligation-evidence.ts`이고 모든 호출이 `?companyId=`를 싣는다(첫 인자 필수).
 *
 * ⛔ 외부 증빙 파일은 **blob으로** 받는다 — 그 purpose는 공개 다운로드 라우트가 `AUTH_ONLY`로 막고 서버 인증이
 * Bearer 전용이라 `<a href>` 내비게이션은 401이다(선례 `organizations.ts`의 `downloadVerificationDocument`).
 */

export const legalObligationEvidenceQueryKeys = {
  evidences: (companyId: string, siteId: string, instanceId: string) =>
    ['legal-documents', companyId, 'sites', siteId, 'instances', instanceId, 'evidences'] as const,
  candidates: (companyId: string, siteId: string, obligationId: string) =>
    ['legal-documents', companyId, 'sites', siteId, 'obligations', obligationId, 'evidence-candidates'] as const,
  /** B+ 2차 — 한 결과에 이어진 결과표(`getLegalObligationResultEvidences`) */
  resultEvidences: (companyId: string, siteId: string, entryId: string) =>
    ['legal-documents', companyId, 'sites', siteId, 'results', entryId, 'evidences'] as const,
};

function unwrap<T>(body: ApiResponse<T>): T {
  if (!body.success || body.data === null) throw body;
  return body.data;
}

function sitePath(siteId: string): string {
  return `/legal-documents/sites/${encodeURIComponent(siteId)}`;
}

export async function getLegalObligationEvidences(
  companyId: string,
  siteId: string,
  instanceId: string
): Promise<LegalObligationEvidencesResponse> {
  const { data } = await api.get<ApiResponse<LegalObligationEvidencesResponse>>(
    `${sitePath(siteId)}/instances/${encodeURIComponent(instanceId)}/evidences`,
    { params: { companyId } }
  );
  return unwrap(data);
}

export async function getLegalObligationEvidenceCandidates(
  companyId: string,
  siteId: string,
  obligationId: string
): Promise<LegalObligationEvidenceCandidatesResponse> {
  const { data } = await api.get<ApiResponse<LegalObligationEvidenceCandidatesResponse>>(
    `${sitePath(siteId)}/obligations/${encodeURIComponent(obligationId)}/evidence-candidates`,
    { params: { companyId } }
  );
  return unwrap(data);
}

/**
 * 인스턴스 온디맨드 실체화 — 멱등. `periodKey`를 생략하면 지금 기간(`currentInstance`가 null인 행에서 증빙을
 * 붙이기 전에 부른다), 적으면 그 지난 기간(L7 — 연간 격자의 「기록 없음」 칸이 쓸 자리). 미래·주기 불일치는 서버가 400.
 */
export async function materializeLegalObligationInstance(
  companyId: string,
  siteId: string,
  obligationId: string,
  periodKey?: string
): Promise<MaterializeLegalObligationInstanceResponse> {
  const body: MaterializeLegalObligationInstanceBody = periodKey ? { periodKey } : {};
  const { data } = await api.post<ApiResponse<MaterializeLegalObligationInstanceResponse>>(
    `${sitePath(siteId)}/obligations/${encodeURIComponent(obligationId)}/instances`,
    // 본문 없는 POST 도 `{}` 로 — `post(url, null)` 은 Content-Type 이 빠져 CORS preflight 에서 막힌다(CLAUDE.md 안티패턴)
    body,
    { params: { companyId } }
  );
  return unwrap(data);
}

export async function attachLegalObligationEvidence(
  companyId: string,
  siteId: string,
  instanceId: string,
  body: AttachLegalObligationEvidenceBody
): Promise<AttachLegalObligationEvidenceResponse> {
  const { data } = await api.post<ApiResponse<AttachLegalObligationEvidenceResponse>>(
    `${sitePath(siteId)}/instances/${encodeURIComponent(instanceId)}/evidences`,
    body,
    { params: { companyId } }
  );
  return unwrap(data);
}

/** 외부 파일 업로드 + 연결(L5) — multipart. 텍스트 칸은 문자열로 간다(서버 DTO가 ISO 날짜를 검증한다) */
export async function uploadLegalObligationEvidenceFile(
  companyId: string,
  siteId: string,
  instanceId: string,
  input: { file: File; fulfilledAt: string; note?: string; resultEntryId?: string }
): Promise<AttachLegalObligationEvidenceResponse> {
  const formData = new FormData();
  formData.append('file', input.file);
  formData.append('fulfilledAt', input.fulfilledAt);
  if (input.note) formData.append('note', input.note);
  // B+ — 어느 측정 결과의 결과표인가(JSON 경로와 같은 칸 · 규칙은 shared `AttachLegalObligationEvidenceBody.resultEntryId`)
  if (input.resultEntryId) formData.append('resultEntryId', input.resultEntryId);
  const { data } = await api.post<ApiResponse<AttachLegalObligationEvidenceResponse>>(
    `${sitePath(siteId)}/instances/${encodeURIComponent(instanceId)}/evidences/file`,
    formData,
    { params: { companyId }, headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return unwrap(data);
}

/**
 * 결과 단위 입구(B+ 2차) — 한 측정·진단 결과에 이어진 결과표 전수(기간을 가로지른다 · 행마다 붙은 기간).
 * 붙일 기간은 서버가 «그 결과 측정일이 든 기간»으로 정하므로 아래 두 등록 함수는 인스턴스를 받지 않는다
 */
export async function getLegalObligationResultEvidences(
  companyId: string,
  siteId: string,
  entryId: string
): Promise<LegalObligationResultEvidencesResponse> {
  const { data } = await api.get<ApiResponse<LegalObligationResultEvidencesResponse>>(
    `${sitePath(siteId)}/results/${encodeURIComponent(entryId)}/evidences`,
    { params: { companyId } }
  );
  return unwrap(data);
}

/** 결과 단위 입구 — 우리 양식 결과표 */
export async function attachLegalObligationResultEvidence(
  companyId: string,
  siteId: string,
  entryId: string,
  body: AttachLegalObligationResultEvidenceBody
): Promise<AttachLegalObligationEvidenceResponse> {
  const { data } = await api.post<ApiResponse<AttachLegalObligationEvidenceResponse>>(
    `${sitePath(siteId)}/results/${encodeURIComponent(entryId)}/evidences`,
    body,
    { params: { companyId } }
  );
  return unwrap(data);
}

/** 결과 단위 입구 — 외부 결과표 파일(multipart) */
export async function uploadLegalObligationResultEvidenceFile(
  companyId: string,
  siteId: string,
  entryId: string,
  input: { file: File; fulfilledAt: string; note?: string }
): Promise<AttachLegalObligationEvidenceResponse> {
  const formData = new FormData();
  formData.append('file', input.file);
  formData.append('fulfilledAt', input.fulfilledAt);
  if (input.note) formData.append('note', input.note);
  const { data } = await api.post<ApiResponse<AttachLegalObligationEvidenceResponse>>(
    `${sitePath(siteId)}/results/${encodeURIComponent(entryId)}/evidences/file`,
    formData,
    { params: { companyId }, headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return unwrap(data);
}

/**
 * 결과표 연결 정정(B+ · 오연결) — 증빙을 다른 측정 결과에 잇거나(`resultEntryId`) 끊는다(`null`). 사유는 필수이고 서버가
 * 감사 기록에 남긴다. 결과를 바꾸면 증빙이 그 결과 측정일의 기간 인스턴스로 옮겨진다(응답 `instanceId`)
 */
export async function relinkLegalObligationEvidenceResult(
  companyId: string,
  siteId: string,
  evidenceId: string,
  body: RelinkLegalObligationEvidenceResultBody
): Promise<RelinkLegalObligationEvidenceResultResponse> {
  const { data } = await api.put<ApiResponse<RelinkLegalObligationEvidenceResultResponse>>(
    `${sitePath(siteId)}/evidences/${encodeURIComponent(evidenceId)}/result-link`,
    body,
    { params: { companyId } }
  );
  return unwrap(data);
}

/** 사건 발생 등록(9-i) — 사건일·기한·내용. 그 사건의 항목이 함께 생긴다(서버 한 트랜잭션) */
export async function registerLegalObligationEvent(
  companyId: string,
  siteId: string,
  obligationId: string,
  body: RegisterLegalObligationEventBody
): Promise<RegisterLegalObligationEventResponse> {
  const { data } = await api.post<ApiResponse<RegisterLegalObligationEventResponse>>(
    `${sitePath(siteId)}/obligations/${encodeURIComponent(obligationId)}/events`,
    body,
    { params: { companyId } }
  );
  return unwrap(data);
}

/** 사건 등록 취소(9-i) — 그 항목에 문서가 있으면 서버가 400 */
export async function revokeLegalObligationEvent(
  companyId: string,
  siteId: string,
  entryId: string
): Promise<{ entryId: string }> {
  const { data } = await api.delete<ApiResponse<{ entryId: string }>>(
    `${sitePath(siteId)}/events/${encodeURIComponent(entryId)}`,
    { params: { companyId } }
  );
  return unwrap(data);
}

/** 마지막 실시일 입력·정정(9-l) — 다음 기한 = 그날 + N년 */
export async function setLegalObligationLastPerformed(
  companyId: string,
  siteId: string,
  obligationId: string,
  body: SetLegalObligationLastPerformedBody
): Promise<SetLegalObligationLastPerformedResponse> {
  const { data } = await api.put<ApiResponse<SetLegalObligationLastPerformedResponse>>(
    `${sitePath(siteId)}/obligations/${encodeURIComponent(obligationId)}/last-performed`,
    body,
    { params: { companyId } }
  );
  return unwrap(data);
}

/** 측정·진단 결과 기록(②-c E7) — 결과 분류가 다음 주기의 간격을 바꾼다 */
export async function recordLegalObligationResult(
  companyId: string,
  siteId: string,
  obligationId: string,
  body: RecordLegalObligationResultBody
): Promise<RecordLegalObligationResultResponse> {
  const { data } = await api.post<ApiResponse<RecordLegalObligationResultResponse>>(
    `${sitePath(siteId)}/obligations/${encodeURIComponent(obligationId)}/results`,
    body,
    { params: { companyId } }
  );
  return unwrap(data);
}

/** 계획일(②-c E8) — 그 날이 든 기간의 인스턴스에 계획일을 적는다(없으면 만든다 · null이면 지운다) */
export async function planLegalObligationInstance(
  companyId: string,
  siteId: string,
  obligationId: string,
  body: PlanLegalObligationInstanceBody
): Promise<PlanLegalObligationInstanceResponse> {
  const { data } = await api.put<ApiResponse<PlanLegalObligationInstanceResponse>>(
    `${sitePath(siteId)}/obligations/${encodeURIComponent(obligationId)}/plan`,
    body,
    { params: { companyId } }
  );
  return unwrap(data);
}

export async function downloadLegalObligationEvidenceFile(
  companyId: string,
  siteId: string,
  evidenceId: string
): Promise<Blob> {
  const response = await api.get(
    `${sitePath(siteId)}/evidences/${encodeURIComponent(evidenceId)}/file`,
    { params: { companyId }, responseType: 'blob' }
  );
  return response.data as Blob;
}
